<template>
  <div class="p-contact__form-container">
    <form
      class="b-block b-block--transparent b-block--sm-pad"
      autocomplete="off"
      @submit.prevent="onSubmitForm">
      <Loader v-if="isLoading"></Loader>
      <Alert
        v-if="!isLoading && errorMsgs.length"
        key="submit-errors"
        dismiss-by-native=""
        type="danger"
        :messages="errorMsgs"
        @close="removeErrors"></Alert>
      <!-- row -->
      <div class="form-floating">
        <input
          id="fullname"
          v-model.trim="fullname"
          name="fullname"
          type="text"
          class="form-control"
          :placeholder="dictionary.fullnamePlaceholder"
          :class="{ 'form-control--error': v$.fullname.$error }"
          @blur="v$.fullname.$touch" />

        <label for="fullname" class="form-label">
          {{ dictionary.fullnameLabel }}
        </label>

        <!-- validations -->
        <template v-if="v$.fullname.$error">
          <div
            v-if="
              v$.fullname.minLength.$invalid || v$.fullname.maxLength.$invalid
            "
            class="field-validation-error">
            {{ dictionary.fullnameLength }}
          </div>
        </template>
      </div>

      <!-- row -->
      <div class="form-floating">
        <input
          id="email"
          v-model.trim="email"
          name="email"
          type="email"
          class="form-control"
          :placeholder="dictionary.emailPlaceholder"
          :class="{ 'form-control--error': v$.email.$error }"
          formnovalidate
          @blur="v$.email.$touch" />

        <label for="email" class="form-label">
          {{ dictionary.emailLabel }}
        </label>

        <!-- validations -->
        <template v-if="v$.email.$error">
          <div v-if="v$.email.email.$invalid" class="field-validation-error">
            {{ dictionary.emailRegex }}
          </div>
        </template>
      </div>

      <!-- row -->
      <div class="form-floating">
        <select
          id="genre"
          v-model.trim="genre"
          name="genre"
          class="form-select"
          :class="{ 'form-select--error': v$.genre.$error }"
          @blur="v$.genre.$touch">
          <option value="">{{ dictionary.genrePlaceholder }}</option>
          <option
            v-for="item in genres"
            :key="item.value"
            :value="item.value"
            :disabled="item.disabled">
            {{ item.text }}
          </option>
        </select>

        <label for="genre" class="form-label">
          {{ dictionary.genreLabel }}
        </label>

        <!-- validations -->
        <template v-if="v$.genre.$error">
          <div v-if="v$.genre.required.$invalid" class="field-validation-error">
            {{ dictionary.genreRequired }}
          </div>
        </template>
      </div>

      <!-- row -->

      <div class="form-floating">
        <textarea
          id="message"
          v-model.trim="message"
          name="message"
          class="form-control"
          :class="{ 'form-control--error': v$.message.$error }"
          :placeholder="dictionary.messagePlaceholder"
          @blur="v$.message.$touch"></textarea>

        <label for="message" class="form-label">
          {{ dictionary.messageLabel }}
        </label>

        <!-- validations -->
        <template v-if="v$.message.$error">
          <div
            v-if="v$.message.required.$invalid"
            class="field-validation-error">
            {{ dictionary.messageRequired }}
          </div>
        </template>
      </div>

      <div class="p-contact__form-message">{{ dictionary.fieldRequired }}</div>

      <!-- row -->
      <div class="b-block__form-btn">
        <button
          type="submit"
          class="p-contact__submit-btn"
          :disabled="submitButtonIsDisabled">
          <span>{{ dictionary.buttonSubmitText }}</span>
        </button>
      </div>
    </form>

    <ModalOverlay
      v-model="showSuccessModal"
      background-class="b-pop-up--alert-success b-pop-up__holder"
      header-background-class="b-pop-up__header">
      <template #header>
        <div class="b-pop-up__header__title">
          {{ dictionary.submitSuccessHeader }}
        </div>
      </template>
      <template #body>
        <div class="b-pop-up__alert-success__content">
          <div
            class="b-pop-up__alert-success__icon b-pop-up__alert-success__icon--success"></div>
          <div class="b-pop-up__alert-success__content__container">
            <div class="b-pop-up__alert-success__title">
              {{ dictionary.submitSuccessTitle }}
            </div>
            <div class="b-pop-up__alert-success__text">
              {{ dictionary.submitSuccessContent }}
            </div>
            <button
              type="button"
              class="b-pop-up__alert-success__cta"
              @click="() => toggleSuccessModal(false)">
              {{ dictionary.submitSuccessClose }}
            </button>
          </div>
        </div>
      </template>
    </ModalOverlay>
  </div>
</template>

<script>
import * as lib from './../../lib/lib'
import Loader from './../components/Shared/Loader.vue'
import Alert from './../components/Shared/Alert.vue'
import useVuelidate from '@vuelidate/core'
import ModalOverlay from './../components/ModalOverlay.vue'
import postRequestHandler from '../../lib/postRequestHandler'
import getErrorMessage from '../../lib/getErrorMessage'

import { email, maxLength, minLength, required } from '@vuelidate/validators'

export default {
  components: { Loader, ModalOverlay, Alert },
  setup() {
    return { v$: useVuelidate() }
  },
  data() {
    return {
      isLoading: true,
      errorMsgs: [],
      showSuccessModal: false,
      // form properties
      email: '',
      fullname: '',
      genre: '',
      genres: [],
      message: '',
    }
  },
  validations() {
    return {
      email: {
        email,
      },
      fullname: {
        minLength: minLength(2),
        maxLength: maxLength(40),
      },
      genre: {
        required,
      },
      message: {
        required,
      },
    }
  },

  computed: {
    submitButtonIsDisabled() {
      return this.isLoading || this.v$.$invalid
    },
  },

  mounted() {
    lib.checkCaptcha(() => (this.isLoading = false))

    const { genres } = lib.getData('contactFormAppData')
    this.genres = genres
    this.setPreselectedOptions()
  },

  methods: {
    removeErrors() {
      this.errorMsgs = []
    },
    toggleSuccessModal(show) {
      this.showSuccessModal = show
    },
    setPreselectedOptions() {
      const { genres } = lib.getData('contactFormAppData')

      // genres
      const entry = genres.find((i) => i.selected)
      if (entry) {
        this.genre = entry.value
      }
    },

    resetUserData() {
      this.v$.$reset()
      this.email = ''
      this.fullname = ''
      this.message = ''
      this.genre = ''
    },

    async onSubmitForm() {
      this.v$.$validate()

      if (this.isLoading) {
        return false
      }

      if (this.v$.$invalid) {
        await this.$nextTick()
        lib.scrollToValidation()
        return false
      }

      this.removeErrors()
      this.isLoading = true

      const postData = {
        email: this.email,
        fullname: this.fullname,
        message: this.message,
        genre: this.genre,
        language:document.documentElement.lang
      }

      await postRequestHandler({
        postData,
        endpoint: this.configuration.endpointSubmitData,
        successCallback: this.handleSuccess,
        errorCallback: this.handleError,
        refreshTokenHandler: this.configuration.refresh,
        captchaAction: this.configuration.captchaAction,
      })
    },

    handleSuccess() {
      this.isLoading = false
      this.removeErrors()
      this.toggleSuccessModal(true)
      this.resetUserData()
    },

    async handleError(messages) {
      this.isLoading = false

      this.errorMsgs = getErrorMessage(
        messages,
        this.dictionary.defaultErrorMessage
      )

      await this.$nextTick()
      lib.scrollToError()
    },
  },
}
</script>
